import { useState, useEffect } from "react";
import { Controller, useController } from "react-hook-form";
// import SetCountyModal from "../../popups/SetCountyModal";
import { useSelector, useDispatch } from "react-redux";
import { setBuyerFormData } from "../../../../store/slices/buyerSlice";
import usePlaceAutocomplete from "../../hooks/usePlaceAutocomplete";

export default function AddressFields({
  mode,
  control,
  FloatingInput,
  // openModal,
  // closeModal,
  setFormData,
  // setValue,
  isInitialLoad,
  showToaster,
  // setCompanyId,
}) {
  const formData = useSelector((state) => state.buyer.formData);
  const dispatch = useDispatch();

  // Local state to track checkbox status
  const [showUnit, setShowUnit] = useState(
    !!formData?.addressMeta?.addressIncludesUnit
  );

  useEffect(() => {
    if (isInitialLoad) return;
    // sync if formData changes externally
    setShowUnit(!!formData?.addressMeta?.addressIncludesUnit);
  }, [formData?.addressMeta?.addressIncludesUnit, isInitialLoad]);

  const {
    query,
    setQuery: setQueryFromHook,
    suggestions,
    // isLoading,
    activeIndex,
    suggestionItemRefs,
    handleChange,
    handleKeyDown,
    handleBlur,
    selectSuggestion,
  } = usePlaceAutocomplete({ country: "us", mode });
  const _setQuery = setQueryFromHook;

  // useController to get direct field setters for programmatic updates
  const addressField = useController({
    name: "addressMeta.address",
    control,
    defaultValue: formData?.addressMeta?.address ?? "",
  });

  const handleSelect = async (item) => {
    const payload = await selectSuggestion(item);
    if (!payload) return;

    const enrichment = payload?.enrichment; // --- NO ENRICHMENT: Only Google address ----------

    if (!enrichment || Object.keys(enrichment).length === 0) {
      //  Update redux
      dispatch(
        setBuyerFormData({
          ...formData,
          addressVerified: false,
          addressMeta: {
            ...formData.addressMeta,
            address: payload.formatted_address,
            city: payload.city,
            state: payload.state,
            zip: payload.zip,
            closing_county: payload.county,
          },
        })
      );

      // 2. Update RHF
      addressField.field.onChange(payload.formatted_address || "");

      showToaster({
        message:
          "The address you entered is incorrect or the State/County is not supported.",
        visible: true,
        type: "error",
      });

      return;
    } else {
      //  console.log("enrichment data found");
      //  console.log(enrichment);

      // ---------- ENRICHMENT AVAILABLE ----------
      const updated = {
        ...formData,
        phase: "Address Field",
        addressVerified: true,
        addressMeta: {
          ...formData.addressMeta,
          address: payload.formatted_address,
          unitNumber: payload.site_unit || "",

          // API data
          city: enrichment.city,
          state: enrichment.state_name,
          state_code: enrichment.state_code,
          county: enrichment.county_name,
          countyid: enrichment.countyid,
          stateid: enrichment.stateid,
          zip: payload.zip,
          companyid: enrichment.companyid,
        },
        estimatedTaxes: {
          ...(formData.estimatedTaxes || {}),
        },

        loanInfoMeta: {
          ...(formData.loanInfoMeta || {}),
          annualPropertyTax: enrichment?.ttb?.assessedValue ?? "0.00",
          assessedValueAmount: enrichment?.ttb?.assessedValue ?? "0.00",
          caTaxRate: enrichment?.ttb?.caTaxRate ?? "0.00",
          adjustedCashPercent: enrichment?.ttb?.adjCashPer ?? "0.00",
          inflationRate: enrichment?.ttb?.infiRate ?? "0.00",
        },
      };
      if (enrichment.mode === "propnine2") {
        setFormData(updated);
        // dispatch(setBuyerFormData(updated));
      }

      // console.log("Updated formData with enrichment:", updated);
      // update input UI
      _setQuery(payload.formatted_address || "");

      // update react-hook-form controlled fields
      addressField.field.onChange(payload.formatted_address || "");
      //  setFormData((prev) => ({ ...prev, addressMeta: { ...prev.addressMeta, ...updated.addressMeta, ...updated.loanInfoMeta } }));
      // setCompanyId(enrichment.companyid);
    }
  };

  return (
    <>
      <div className="calc-frm-field full-dvd fsapce-top fsapce-top fsapce-bottom">
        <div className="calc-frm-fld form-group checkbox-fld">
          {/* address includes unit checkbox stored under address.addressIncludesUnit */}
          <Controller
            name="addressMeta.addressIncludesUnit"
            control={control}
            defaultValue={!!formData?.addressMeta?.addressIncludesUnit}
            render={({ field }) => (
              <p>
                <input
                  type="checkbox"
                  checked={!!field.value}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    field.onChange(checked);
                    setShowUnit(checked); // ✅ sync with local state
                  }}
                />{" "}
                Address includes a Unit
              </p>
            )}
          />
        </div>
        <div className="calc-frm-fld form-group"></div>
      </div>

      <div className="calc-frm-field">
        <div className="calc-frm-fld form-group input-sticky">
          <FloatingInput
            {...addressField.field}
            placeholder="Enter a location"
            label="Current Home Address"
            type="text"
            className="input-filled"
            value={query || addressField.field.value || ""}
            onChange={(e) => {
              const v = e.target.value;
              try {
                addressField.field.onChange(v);
              } catch (err) {}
              handleChange(v);
              _setQuery(v);
            }}
            onKeyDown={(e) => {
              handleKeyDown(e);
              // ensure Enter triggers the same component-level select flow
              if (
                e.key === "Enter" &&
                typeof activeIndex === "number" &&
                activeIndex >= 0
              ) {
                try {
                  e.preventDefault();
                  const selected = suggestions[activeIndex];
                  if (selected) handleSelect(selected);
                } catch (err) {
                  console.warn(
                    "Buyer AddressFields keyboard select failed",
                    err
                  );
                }
              }
            }}
            onBlur={() => {
              handleBlur();
              try {
                addressField.field.onBlur();
              } catch (e) {}
            }}
          />

          {suggestions && suggestions.length > 0 && (
            <div className="search-list-prop">
              {suggestions.map((s, idx) => (
                <div
                  key={idx}
                  ref={(el) => (suggestionItemRefs.current[idx] = el)}
                  className={`search-list-prop-wrap ${
                    idx === activeIndex ? "" : ""
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(s);
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    handleSelect(s);
                  }}
                >
                  <div className="search-list-prop-heading">
                    {s.description || s.structured_formatting?.main_text}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ✅ Only show Unit No. input if checkbox is checked */}
        {showUnit && (
          <div className="calc-frm-fld form-group small-input">
            <Controller
              name="addressMeta.unitNumber"
              control={control}
              defaultValue={formData?.addressMeta?.unitNumber ?? ""}
              render={({ field }) => (
                <FloatingInput
                  {...field}
                  placeholder="Unit No."
                  label="Unit No."
                  type="text"
                  className="input-filled"
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={(e) => {
                    setFormData((p) => ({
                      ...p,
                      addressMeta: {
                        ...p.addressMeta,
                        unitNumber: e.target.value,
                      },
                    }));
                  }}
                />
              )}
            />
          </div>
        )}
      </div>

      {/*  <div className="calc-frm-field full-dvd">
        <div className="calc-frm-fld form-group">
          <FloatingInput
            {...cityField.field}
            value={cityField.field.value || ''}
            placeholder=""
            label="City"
            type="text"
            onChange={(e) => cityField.field.onChange(e.target.value)}
            onBlur={(e) => {
              setFormData((p) => ({
                ...p,
                addressMeta: {
                  ...p.addressMeta,
                  city: e.target.value,
                },
              }))
            }}
          />
        </div>
        <div className="calc-frm-fld form-group">
          <div className="calc-frm-field">
            <div className="calc-frm-fld form-group no-m-space">
              <FloatingInput
                {...stateField.field}
                value={stateField.field.value || ''}
                placeholder=""
                label="State"
                type="text"
                onChange={(e) => stateField.field.onChange(e.target.value)}
                onBlur={(e) => {
                  setFormData((p) => ({
                    ...p,
                    addressMeta: {
                      ...p.addressMeta,
                      state: e.target.value,
                    },
                  }))
                }}
              />
            </div>
            <div className="calc-frm-fld form-group no-m-space">
              <FloatingInput
                {...zipField.field}
                value={zipField.field.value || ""}
                placeholder=""
                label="Zip"
                type="text"            // IMPORTANT: use text, not number
                maxLength={6}          // prevents entering more than 6 digits
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, ""); // keep digits only
                  zipField.field.onChange(val);
                }}
                onBlur={(e) => {
                  setFormData((p) => ({
                    ...p,
                    addressMeta: {
                      ...p.addressMeta,
                      zip: e.target.value.replace(/\D/g, ""),
                    },
                  }))
                }}
              />

            </div>
          </div>
        </div>
      </div> */}
    </>
  );
}
